<script setup>
import { ref, computed } from 'vue';
import { useUiStore } from '@/store/uiStore';
import { storeToRefs } from 'pinia'
import { useRoute, RouterView } from 'vue-router';
import Dashboard from '@/modules/gestion/views/dashboard/Dashboard.vue';

const tabs = ref([])
const activeTabId = ref(null)
const nextId = ref(1)
const route = useRoute();

const ui = useUiStore()
const { sidebarColor } = storeToRefs(ui)

const colorMap = {
    'primary': '#AEC6CF', // Azul pastel
    'secondary': '#CFBFCF', // Gris rosado suave
    'success': '#B2F1B2', // Verde menta
    'danger': '#FFB3B3', // Rosa suave
    'warning': '#FFFACD', // Amarillo crema
    'info': '#B3E6FF', // Celeste
    'light': '#F5F5DC', // Beige claro
    'dark': '#B8B8DC', // Lavanda suave
    'default': '#CFBFCF' // Gris rosado suave
};

// Computed para verificar si hay pestañas
const hasTabs = computed(() => tabs.value.length > 0)

// Computed para verificar si hay una ruta directa de Vue Router
const hasDirectRoute = computed(() => {
    return route.path.startsWith('/admin') || route.path.startsWith('/gestion')
})

// Computed para obtener el color actual de forma reactiva
const currentColor = computed(() => {
    return colorMap[sidebarColor.value] || colorMap.default
})

function openTab(title, url) {
    const existing = tabs.value.find((tab) => tab.url === url)
    if (existing) {
        activeTabId.value = existing.id
    } else {
        const id = nextId.value++
        tabs.value.push({ id, title, url })
        activeTabId.value = id
    }
}

function selectTab(id) {
    activeTabId.value = id
}

function closeTab(id) {
    const index = tabs.value.findIndex((tab) => tab.id === id)
    if (index !== -1) {
        tabs.value.splice(index, 1)
        if (activeTabId.value === id) {
            const nextTab = tabs.value[index] || tabs.value[index - 1]
            activeTabId.value = nextTab?.id || null
        }
    }
}

// Exponer funciones para que el padre pueda llamarlas
defineExpose({ openTab })
</script>

<template>
    <div class="admin-tabs">
        <!-- Barra de pestañas - solo mostrar si hay pestañas -->
        <div v-if="hasTabs" class="tabs-bar">
            <div v-for="tab in tabs" :key="tab.id" :class="['tab', { active: tab.id === activeTabId }]"
                :style="tab.id === activeTabId ? { backgroundColor: currentColor } : {}" @click="selectTab(tab.id)">
                {{ tab.title }}
                <button class="close-btn" @click.stop="closeTab(tab.id)">✖</button>
            </div>
        </div>

        <!-- Contenedor del contenido -->
        <div class="content-container">
            <!-- Ruta directa de Vue Router -->
            <div v-if="hasDirectRoute && !hasTabs" class="direct-route-container">
                <RouterView />
            </div>

            <!-- Dashboard por defecto cuando no hay pestañas ni rutas directas -->
            <div v-else-if="!hasTabs && !hasDirectRoute" class="dashboard-container">
                <Dashboard />
            </div>

            <!-- Contenedor del iframe para las pestañas -->
            <div v-else class="iframe-container">
                <iframe v-for="tab in tabs" :key="tab.id" v-show="tab.id === activeTabId" :src="tab.url" frameborder="0"
                    class="iframe-view">
                </iframe>
            </div>
        </div>
    </div>
</template>

<style scoped>
.admin-tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.tabs-bar {
    display: flex;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
    padding: 0 8px;
}

.tab {
    padding: 8px 16px;
    cursor: pointer;
    margin-right: 4px;
    position: relative;
    border-radius: 8px 8px 0 0;
    transition: background-color 0.2s ease;
}

.tab.active {
    border: 1px solid #ccc;
    border-bottom: none;
    z-index: 1;
}

.close-btn {
    margin-left: 8px;
    color: red;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
}

.close-btn:hover {
    color: darkred;
}

.content-container {
    flex: 1;
    overflow: hidden;
}

.dashboard-container,
.direct-route-container {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding: 20px;
}

.iframe-container {
    flex: 1;
    overflow: hidden;
}

.iframe-view {
    width: 100%;
    height: 80vh;
}
</style>